"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from '@/shared/ui/icons';

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { cn } from "./utils";
import { SURFACE_CLOSE_TARGET } from './surface-controls';

const DialogModule = DialogPrimitive as any;
const DialogDefault = DialogModule?.default;
const RadixDialog = Object.assign(
  {},
  typeof DialogDefault === "object" ? DialogDefault : {},
  DialogModule,
);
RadixDialog.Root =
  DialogModule?.Root ??
  DialogDefault?.Root ??
  (typeof DialogDefault === "function" || typeof DialogDefault === "object"
    ? DialogDefault
    : undefined);
RadixDialog.Trigger = DialogModule?.Trigger ?? DialogModule?.default?.Trigger;
RadixDialog.Content = DialogModule?.Content ?? DialogModule?.default?.Content;
RadixDialog.Portal = DialogModule?.Portal ?? DialogModule?.default?.Portal;
RadixDialog.Overlay = DialogModule?.Overlay ?? DialogModule?.default?.Overlay;
RadixDialog.Close = DialogModule?.Close ?? DialogModule?.default?.Close;
RadixDialog.Title = DialogModule?.Title ?? DialogModule?.default?.Title;
RadixDialog.Description = DialogModule?.Description ?? DialogModule?.default?.Description;

const hasRadixDialog =
  typeof (RadixDialog as any)?.Root !== "undefined" &&
  typeof (RadixDialog as any)?.Trigger !== "undefined" &&
  typeof (RadixDialog as any)?.Content !== "undefined";

let hasWarnedMissingRadixDialog = false;
const warnMissingRadixDialog = () => {
  if (hasWarnedMissingRadixDialog) return;
  hasWarnedMissingRadixDialog = true;
  console.warn(
    "[ui/dialog] Radix Dialog primitives unavailable. Falling back to basic markup.",
  );
};

// Robust helper to filter out ANY Generated-injected debug props
const filterGeneratedProps = (props: any) => {
  const clean: any = {};
  for (const key in props) {
    if (!key.startsWith("_fg")) {
      clean[key] = props[key];
    }
  }
  return clean;
};

const SafeIcon = ({ icon: Icon, ...props }: { icon: any, [key: string]: any }) => {
  const clean = filterGeneratedProps(props);
  return <Icon {...clean} />;
};

function Dialog({
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixDialog) {
    warnMissingRadixDialog();
    const isOpen = (cleanProps as any)?.open;
    if (isOpen === false) return null;
    return (
      <div data-slot="dialog-fallback" {...(cleanProps as any)}>
        {children}
      </div>
    );
  }
  return (
    <RadixDialog.Root data-slot="dialog" {...cleanProps}>
      {children}
    </RadixDialog.Root>
  );
}

const DialogTrigger = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Trigger>,
  React.ComponentProps<typeof DialogPrimitive.Trigger>
>(({ ...props }, ref) => {
  const cleanProps = filterGeneratedProps(props);
  return hasRadixDialog && typeof (RadixDialog as any)?.Trigger !== "undefined" ? (
    <RadixDialog.Trigger ref={ref} data-slot="dialog-trigger" {...cleanProps} />
  ) : (
    <button ref={ref as any} data-slot="dialog-trigger-fallback" {...(cleanProps as any)} />
  );
});

DialogTrigger.displayName = "DialogTrigger";

function DialogPortal({
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixDialog || typeof (RadixDialog as any)?.Portal === "undefined") {
    warnMissingRadixDialog();
    return (
      <div data-slot="dialog-portal-fallback" {...(cleanProps as any)}>
        {children}
      </div>
    );
  }
  return (
    <RadixDialog.Portal data-slot="dialog-portal" {...cleanProps}>
      {children}
    </RadixDialog.Portal>
  );
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixDialog || typeof (RadixDialog as any)?.Close === "undefined") {
    warnMissingRadixDialog();
    return <button data-slot="dialog-close-fallback" {...(cleanProps as any)} />;
  }
  return <RadixDialog.Close data-slot="dialog-close" {...cleanProps} />;
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentProps<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixDialog || typeof (RadixDialog as any)?.Overlay === "undefined") {
    warnMissingRadixDialog();
    return null;
  }
  return (
    <RadixDialog.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[220] bg-black/50",
        className,
      )}
      {...cleanProps}
    />
  );
});

DialogOverlay.displayName = "DialogOverlay";

function DialogContent({
  className,
  overlayClassName,
  hideClose,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & NativeMetadataProps & {
  /**
   * Override the scrim overlay's className. Pass `"bg-transparent"` for a
   * non-blocking presentation where the page underneath stays visible —
   * Apple's macOS sheet pattern. Default keeps the standard `bg-black/50`.
   */
  overlayClassName?: string;
  /**
   * Hide the built-in top-right close button. Use when the dialog provides
   * its own dismiss control (e.g. a Cancel button) so the `×` isn't
   * redundant. Default keeps the close button.
   */
  hideClose?: boolean;
}) {
  const cleanProps = filterGeneratedProps(props);
  const { nativeProps, rest } = splitNativeProps(cleanProps);
  if (!hasRadixDialog || typeof (RadixDialog as any)?.Content === "undefined") {
    warnMissingRadixDialog();
    const fallback = (
      <>
        <div
          data-slot="dialog-overlay-fallback"
          className={cn("fixed inset-0 z-[300] bg-black/50", overlayClassName)}
        />
        <div
          data-slot="dialog-content-fallback"
          data-native-role={nativeProps["data-native-role"] ?? "dialog"}
          data-native-presentation={nativeProps["data-native-presentation"] ?? "dialog"}
          className={cn(
            "bg-background fixed top-1/2 left-1/2 z-[310] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-[var(--ui-dialog-radius)] border p-6 shadow-[var(--ui-dialog-shadow)]",
            className,
          )}
          {...nativeProps}
          {...(rest as any)}
        >
          {children}
        </div>
      </>
    );
    if (typeof document !== "undefined") {
      return createPortal(fallback, document.body);
    }
    return fallback;
  }
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} />
      <RadixDialog.Content
        data-slot="dialog-content"
        data-native-role={nativeProps["data-native-role"] ?? "dialog"}
        data-native-presentation={nativeProps["data-native-presentation"] ?? "dialog"}
        className={cn(
          "qs-vibrant-fill data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 !fixed top-[50%] left-[50%] z-[230] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-[20px] p-[20px] duration-200 sm:max-w-lg",
          // Apple macOS 26 dialog spec — outer 10 px radius with the
          // window's stronger inner radius implied by `--dialog-shadow`
          // (1 px hairline border + heavy 16 / 48 drop shadow). Replaces
          // the previous `rounded-lg border glass-shadow-strong` combo.
          "rounded-[var(--ui-dialog-radius)] shadow-[var(--ui-dialog-shadow)]",
          className,
        )}
        aria-describedby={(rest as any)["aria-describedby"] || undefined}
        {...nativeProps}
        {...rest}
      >
        {children}
        {!hideClose && (
          <RadixDialog.Close className={cn(SURFACE_CLOSE_TARGET, "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4")}>
            <SafeIcon icon={XIcon} />
            <span className="sr-only">Close</span>
          </RadixDialog.Close>
        )}
      </RadixDialog.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...cleanProps}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...cleanProps}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixDialog || typeof (RadixDialog as any)?.Title === "undefined") {
    warnMissingRadixDialog();
    return (
      <div
        data-slot="dialog-title-fallback"
        className={cn("text-lg leading-none font-semibold", className)}
        {...(cleanProps as any)}
      />
    );
  }
  return (
    <RadixDialog.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...cleanProps}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixDialog || typeof (RadixDialog as any)?.Description === "undefined") {
    warnMissingRadixDialog();
    return (
      <div
        data-slot="dialog-description-fallback"
        className={cn("text-muted-foreground text-sm", className)}
        {...(cleanProps as any)}
      />
    );
  }
  return (
    <RadixDialog.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...cleanProps}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
